import { Inject, Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, firstValueFrom, from, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, timeout } from 'rxjs/operators';
import {
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalInterceptorConfiguration,
  MsalService,
} from '@azure/msal-angular';
import { AccountInfo, InteractionStatus } from '@azure/msal-browser';
import { describeActiveAccount } from './active-account';
import { GoogleIdentityService } from './google-identity.service';
import { ExternalIdSignInService } from './external-id-sign-in.service';
import { AuthStatusService } from './auth-status.service';
import {
  CLIENT_MARKER_HEADER,
  clientMarker,
  isAnonymousApiUrl,
  isVericureApiUrl,
} from './client-marker';
import {
  clearApiScopeRedirectLatch,
  isInteractionRequired,
  redirectForApiScope,
  requireEntraAuth,
} from './api-scope';
import { authFailureBody } from './auth-failure';

/**
 * Ceiling on waiting for MSAL to initialise.
 *
 * Generous, because the cost of waiting is a slightly later first data call
 * while the cost of NOT waiting is a token-less request that 403s. Finite,
 * because an initialisation that never completes must not hang every request.
 */
const MSAL_SETTLE_TIMEOUT_MS = 10000;

/**
 * Replacement for the stock `MsalInterceptor`.
 *
 * Why not MsalInterceptor
 * -----------------------
 * MsalInterceptor attaches a token to every URL in `protectedResourceMap`
 * unconditionally: if it cannot get one silently it escalates to INTERACTIVE
 * authentication — a full redirect to Microsoft. Vericure Hybrid has three sign-in
 * providers (Entra/MSAL, Google, email+password), and only the first has an MSAL
 * account. So the moment VericureDataServices URLs enter the map, every Google and
 * email+password user gets bounced to a Microsoft login on their first data call.
 *
 * What this does instead
 * ----------------------
 * Same matching rules, same config object — but the token is *best effort*:
 *
 *   no matching URL          -> pass through untouched
 *   no MSAL account          -> pass through untouched   (function-key path)
 *   silent acquisition fails -> pass through untouched   (function-key path)
 *   token acquired           -> attach `Authorization: Bearer <token>`
 *
 * It NEVER calls an interactive method (`acquireTokenRedirect`, `acquireTokenPopup`,
 * `loginRedirect`, `loginPopup`), so a non-MSAL session can never be redirected to
 * Microsoft by an HTTP call. That holds whether RequireEntraAuth is on or off, which
 * is what makes flipping the flag safe for multi-provider tenants.
 *
 * The function-key query parameter (`?code=`) is on the URL and is never touched, so
 * the two auth paths overlap for as long as the server accepts either.
 *
 * This supports both eventual product directions: keep multiple providers (Entra
 * users get bearers, others keep the function key), or go Entra-only (every session
 * has an account, so every call gets a bearer) — with no further client change.
 */
@Injectable()
export class VericureAuthInterceptor implements HttpInterceptor {
  constructor(
    @Inject(MSAL_INTERCEPTOR_CONFIG)
    private readonly msalInterceptorConfig: MsalInterceptorConfiguration,
    private readonly msalService: MsalService,
    private readonly msalBroadcast: MsalBroadcastService,
    private readonly authStatus: AuthStatusService,
    private readonly googleIdentity: GoogleIdentityService,
    private readonly externalId: ExternalIdSignInService
  ) {}

  /** Every intercept() call since load. One per HttpClient SUBSCRIPTION. */
  private static interceptCount = 0;
  /** next.handle() invocations, keyed by intercept id. */
  private static sendsPerIntercept = new Map<number, number>();

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    // STAMP FIRST, before anything can return early.
    //
    // Deliberately ahead of the protectedResourceMap check and independent of
    // requireEntraAuth: the marker has to be on calls that FAIL as much as ones
    // that succeed, because during an incident the interesting requests are the
    // 401s and 403s, and DataServices logs the header before its auth decision.
    // Placing it after the scope check would have dropped it in every config
    // where the flag is off.
    req = this.stampClient(req);

    // ANONYMOUS ENDPOINTS GO OUT BARE: no session check, no MSAL wait, no token
    // acquired, and no Authorization header even when a session EXISTS. The server
    // ignores identity there, and a credential you do not need to send is one you
    // should not send. See ANONYMOUS_API_PATHS for why the list is exact.
    if (isAnonymousApiUrl(req.url)) {
      return next.handle(req.headers.has('Authorization') ? req.clone({ headers: req.headers.delete('Authorization') }) : req);
    }

    const scopes = this.scopesFor(req.url);
    if (!scopes || scopes.length === 0) {
      return next.handle(req);
    }

    // THE DISCRIMINATOR for "one loadX call, two GETs on the wire".
    //
    // http.get() is COLD: every subscription re-runs this whole chain, so two
    // subscriptions produce two intercept ids and two sends. An interceptor that
    // re-issues instead produces ONE id and two sends. The ids separate those
    // two causes without needing another capture to guess between them.
    const reqId = ++VericureAuthInterceptor.interceptCount;
    console.log(`HTTP-INTERCEPT #${reqId} ${this.endpointOf(req.url)}`);

    // WAIT FOR MSAL TO SETTLE FIRST.
    //
    // This is what made a VALID session look expired. On a reload the app's data
    // calls go out immediately, while MSAL is still initialising and handling the
    // redirect promise. Until that finishes getAllAccounts() is EMPTY, so the
    // check below found no account, took the Google/password branch, and sent the
    // request with no bearer at all — silently, without even the acquisition
    // warning, because there was no acquisition to fail. An Entra-tied project
    // answers that with 403.
    //
    // The session was fine the whole time; we were asking before MSAL could
    // answer. Waiting for InteractionStatus.None costs nothing once settled — the
    // subject replays its current value — and only delays the very first calls
    // after a load.
    return from(this.settled()).pipe(
      switchMap(() => {
        const account = this.activeAccount();
        if (!account) {
          // GOOGLE SESSION FIRST. No MSAL account is the NORMAL, healthy state for a Google
          // sign-in, not a failure — so it is checked before anything warns or refuses.
          //
          // This is where the Google fallthrough belongs and originally did not: it was written
          // 133 lines further down, inside the path that only runs when an account EXISTS. A
          // Google-only session hit the refusal below and never reached it, so every API call
          // would have been refused the moment a Google client id was configured — sign-in
          // appearing to succeed and then nothing loading. Caught by a spec written to cover this
          // exact path; ng build passed throughout.
          //
          // There is no silent renewal to attempt: a browser public client cannot refresh a
          // Google ID token. getIdToken() returns null within 60s of expiry, which lands the
          // caller in the refusal below — the intended interactive re-auth, not a bug.
          // SCOPED TO OUR OWN API, not to everything in protectedResourceMap. That map also
          // covers graph.microsoft.com, so an unscoped attach would send a Google ID token to
          // MICROSOFT on any Graph call from a Google session. Graph would reject it, so nothing
          // would break — which is exactly why it would have gone unnoticed. It is still our
          // user's signed identity assertion (email, sub, hd) handed to an unrelated third party
          // for no reason, and a credential you did not need to send is one you should not send.
          //
          // isVericureApiUrl is the same predicate stampClient uses to keep the X-Vericure-Client
          // header off third-party hosts, so the two agree on what "our API" means by construction
          // rather than by two similar-looking checks drifting apart.
          //
          // CONSEQUENCE, deliberate: Graph is unreachable for a Google-only session. Nothing in
          // the app calls Graph on that path today (Graph is MSAL's User.Read), and a Google
          // session has no Microsoft identity to call it with anyway.
          const googleToken = isVericureApiUrl(req.url)
            ? this.googleIdentity.getIdToken()
            : null;

          if (googleToken) {
            // NOTE THE ASYMMETRY WITH THE MSAL PATH, WHICH IS CHOSEN, NOT OVERLOOKED: the Entra
            // path calls authStatus.signedIn(account) here and this does not.
            // AuthStatusService.signedIn takes an MSAL AccountInfo, which a Google session does
            // not have and must not fabricate — inventing an account object to satisfy a type is
            // how the deleted prototype ended up asserting a literal 'google-default-tenant'.
            // Google session state lives on GoogleIdentityService.signedIn$ instead.
            //
            // FOR WHOEVER WIRES THE HEADER UI: anything that renders "signed in" from
            // AuthStatusService alone will show a Google session as signed out. It needs to read
            // both sources, or AuthStatusService needs a provider-neutral shape. Out of scope here
            // — flagged so it is a known gap rather than a surprise.
            this.logSend(req.url, true, reqId);
            return next.handle(this.withBearer(req, googleToken));
          }

          // EXTERNAL ID (CIAM) SESSION NEXT. Same placement and the same scoping as Google, for the
          // same reasons: no MSAL (workforce) account is the normal state for a CIAM session, and the
          // token goes to OUR API only, never Graph. The CIAM instance keeps its accounts in
          // sessionStorage, which the workforce instance never reads, so a CIAM user never shows up
          // as `account` above (see external-id-sign-in.service.ts). accessToken() is silent only;
          // null falls through to the no-session handling, exactly as before this branch existed.
          if (isVericureApiUrl(req.url) && this.externalId.enabled()) {
            return from(this.externalId.accessToken()).pipe(
              switchMap((ciamToken) => {
                // The header follows the token, as on the workforce path: a live token is signed in;
                // a known CIAM account that cannot get one is an expired session, not "signed out".
                const ciamAccount = this.externalId.account();
                if (ciamToken) {
                  if (ciamAccount) this.authStatus.signedIn(ciamAccount);
                  this.logSend(req.url, true, reqId);
                  return next.handle(this.withBearer(req, ciamToken));
                }
                if (ciamAccount) this.authStatus.tokenRefreshFailed(ciamAccount, 'External ID token could not be renewed');
                return this.noSession(req, next, reqId);
              })
            );
          }
          return this.noSession(req, next, reqId);
        }
        return this.withAcquiredToken(req, next, scopes, account, reqId);
      })
    );
  }

  /**
   * Log every request that actually leaves, with whether it carried a bearer.
   *
   * The loader-level trace could not see this: it logs where a fetch is DECIDED,
   * and the duplicate was being created further down, inside the HTTP pipeline.
   * Instrumenting the send is what makes the two counts reconcilable — one line
   * here per GET on the wire.
   */
  private logSend(url: string, withBearer: boolean, reqId = 0): void {
    const sends = (VericureAuthInterceptor.sendsPerIntercept.get(reqId) ?? 0) + 1;
    VericureAuthInterceptor.sendsPerIntercept.set(reqId, sends);
    if (sends > 1) {
      // One intercept, more than one send: the request is being RE-ISSUED
      // inside this interceptor rather than re-subscribed from above.
      console.error(
        `HTTP-RESEND intercept #${reqId} has now sent ${sends} times — ${url}`
      );
    }
    const endpoint = url.split('?')[0].split('/').pop() ?? url;
    // isRealtime is the ONLY thing distinguishing a historical backfill from a
    // live poll — both families use the same endpoint. Without it in the log,
    // "GetTemperatureData x2" reads as a duplicate load when it is in fact one
    // backfill plus the live stream doing its job.
    const realtime = /[?&]isRealtime=([^&]*)/.exec(url)?.[1] ?? '-';
    console.log(
      `HTTP-SEND #${reqId}.${sends} ${endpoint} isRealtime=${realtime} ` +
        `bearer=${withBearer ? 'yes' : 'NO'}`
    );
  }

  /** Endpoint name only, for log lines that do not need the query string. */
  private endpointOf(url: string): string {
    return url.split('?')[0].split('/').pop() ?? url;
  }

  /** Resolves once MSAL is no longer mid-flight. */
  private async settled(): Promise<void> {
    try {
      await firstValueFrom(
        this.msalBroadcast.inProgress$.pipe(
          filter((status) => status === InteractionStatus.None),
          take(1),
          timeout(MSAL_SETTLE_TIMEOUT_MS)
        )
      );
    } catch {
      // Never block a request forever on an init that never completes; a
      // token-less attempt still has the function-key path behind it.
      console.warn('VericureAuth: MSAL did not settle in time; proceeding');
    }
  }

  /** No workforce account, no Google token, no External ID token. Unchanged behaviour. */
  private noSession(
    req: HttpRequest<unknown>,
    next: HttpHandler,
    reqId: number
  ): Observable<HttpEvent<unknown>> {
    // Genuinely no MSAL account: Google or email+password. No Entra token
    // exists to get, and we must NOT trigger interactive auth.
    //
    // THIS BRANCH USED TO BE COMPLETELY SILENT, and that silence is why an
    // Entra session with an emptied MSAL cache was indistinguishable from a
    // Google session: no bearer, no warning, no redirect, just a 403 that
    // looked like a server problem. It is a legitimate path for two of the
    // three sign-in providers, so it cannot redirect — but it can say what
    // it did.
    //
    // Logged once per session rather than per request: a page load makes
    // many data calls and a wall of identical warnings buries the signal.
    if (!this.warnedNoAccount) {
      this.warnedNoAccount = true;
      console.warn(
        `VericureAuth: NO MSAL ACCOUNT when calling ${req.url} — sending ` +
          `no bearer AND NO FALLBACK — the function key is gone. ` +
          `Expected for Google or ` +
          `email+password sessions. If this IS an Entra session, MSAL has ` +
          `no cached account: its localStorage was cleared, or sign-in has ` +
          `not completed. Accounts=` +
          `${this.accountCount()}, inProgress gate passed.`
      );
    }
    if (requireEntraAuth()) {
      return throwError(() => this.refuseUnauthenticated(req, 'no MSAL account'));
    }
    this.logSend(req.url, false, reqId);
    return next.handle(req);
  }

  private withAcquiredToken(
    req: HttpRequest<unknown>,
    next: HttpHandler,
    scopes: string[],
    account: unknown,
    reqId: number
  ): Observable<HttpEvent<unknown>> {
    return from(
      this.acquireSilently(scopes, account).catch((err) => {
        // DEGRADING SILENTLY IS HOW THIS HID. A token-less request to an
        // Entra-tied project comes back 403 with a message about a missing
        // bearer, which reads as a server authz problem rather than as an
        // acquisition that quietly failed on the client. Say so.
        console.warn(
          `VericureAuth: NO BEARER for ${req.url} — the request will go out ` +
            `UNAUTHENTICATED and the server will refuse it. There is no function ` +
            `key to fall back to any more. ` +
            `${(err as { errorCode?: string })?.errorCode ?? err}`
        );
        // REQUIREMENT 2: a genuine expiry gets a redirect, not a silent 403.
        // Only for real interaction_required — a transient failure must not
        // bounce the user out of the app. redirectForApiScope carries the
        // current URL, so re-auth returns to this project and tab.
        // THE STATE THAT USED TO LOOK LIKE A SERVER OUTAGE. We know the user;
        // MSAL will not renew their token. Say that, in those words, where they
        // can see it.
        this.authStatus.tokenRefreshFailed(
          account as AccountInfo,
          (err as { errorCode?: string })?.errorCode
        );
        // RECOVER FROM ANY FAILED ACQUISITION, not only a formally
        // interaction_required one.
        //
        // The narrow test was the gap Joel fell through: MSAL has plenty of ways
        // to fail to renew a token that are not tagged interaction_required, and
        // for every one of them the user saw "the request was not sent" with a
        // Try again button that could not help. If we hold an account and cannot
        // get it a token, the honest move is to ask the user to sign in.
        //
        // Unbounded bouncing is the risk, and redirectForApiScope already owns
        // the bound: a per-account budget of API_SCOPE_MAX_REDIRECTS. It returns
        // false when that is spent, which is exactly the terminal signal the UI
        // needs — so recovery and loop protection stay in ONE place rather than
        // being reimplemented here.
        //
        // WITH the account. A redirect that does not name the identity whose
        // acquisition just failed re-grants the scope to whoever the browser's
        // Microsoft session prefers, leaves the original lookup missing, and
        // fails identically next time round.
        const started = redirectForApiScope(
          this.msalService.instance,
          isInteractionRequired(err) ? req.url : `${req.url} (${(err as { errorCode?: string })?.errorCode ?? 'acquisition failed'})`,
          account as AccountInfo
        );
        this.recoveryExhausted = !started;
        return null;
      })
    ).pipe(
      switchMap((accessToken) => {
        if (accessToken) {
          // A live token is the only proof the session actually works; anything
          // earlier is an assumption.
          this.authStatus.signedIn(account as AccountInfo);
        }
        // NO GOOGLE FALLTHROUGH HERE, DELIBERATELY. Reaching this point means an MSAL account
        // EXISTS and its silent acquisition failed. Falling through to a Google token there would
        // change WHICH IDENTITY the caller is, silently, because a Microsoft token refresh failed
        // — a different person's company scope, from a transient network fault. An Entra session
        // whose token cannot be renewed must re-authenticate as itself.
        //
        // The Google path is the NO-ACCOUNT branch above, which is the state a Google-only session
        // is actually in.
        if (!accessToken && requireEntraAuth()) {
          return throwError(() =>
            this.refuseUnauthenticated(
              req,
              'silent token acquisition failed',
              this.recoveryExhausted
            )
          );
        }
        // ONE send per request. See below for why this is worth saying.
        this.logSend(req.url, !!accessToken, reqId);
        return next.handle(accessToken ? this.withBearer(req, accessToken) : req);
      })
      // NO catchError RE-SEND HERE.
      //
      // This used to end with `catchError(() => next.handle(req))`, intended as
      // "if acquisition failed, send anyway". But it sits AFTER the switchMap, so
      // it caught errors from the HTTP REQUEST too — and answered them by issuing
      // the whole request a second time, without the bearer. That is the 2x on
      // the initial load: one logical fetch, two GETs, and only one dedupe log,
      // because the re-send happens below the dedupe entirely.
      //
      // It was also masking failures: the retry's result replaced the original
      // error, so callers never saw what actually went wrong.
      //
      // Acquisition failure is already handled where it happens —
      // acquireSilently().catch() yields a null token and the request goes out
      // once, unauthenticated. An HTTP error is the server's answer and belongs
      // to the caller.
    );
  }

  /** Scopes configured for this URL, by prefix match, or undefined. */
  private scopesFor(url: string): string[] | undefined {
    const map = this.msalInterceptorConfig?.protectedResourceMap;
    if (!map || typeof map.forEach !== 'function') {
      return undefined;
    }
    let matched: string[] | undefined;
    let matchedKeyLength = -1;
    map.forEach((scopes, key) => {
      // Longest matching prefix wins, so a more specific entry beats a broader one.
      if (
        typeof key === 'string' &&
        url.startsWith(key) &&
        key.length > matchedKeyLength
      ) {
        matched = (scopes as string[]) ?? undefined;
        matchedKeyLength = key.length;
      }
    });
    return matched;
  }

  /**
   * The signed-in MSAL account, or null for a non-MSAL session.
   *
   * THIS USED TO END `all.length > 0 ? all[0] : null`, and that subscript is what
   * anchored token acquisition to the wrong user. With two cached accounts and no
   * active one it returned whichever the MSAL cache listed first — joel@vericure.io
   * — so the silent authorize request carried that identity's sid and
   * X-AnchorMailbox while the user was signing in as joel@vortexcompanies.com.
   *
   * resolveActiveAccount returns null rather than guessing between accounts, and
   * null here means the request is refused with something the user can act on
   * instead of being sent as somebody else.
   */
  private activeAccount(): unknown | null {
    const resolution = describeActiveAccount(this.msalService?.instance);
    // A data call is often where a stranded session first shows itself, so this
    // is a real opportunity to say so rather than only refusing the request.
    if (resolution.kind === 'ambiguous') {
      this.authStatus.ambiguous(resolution.candidates);
    }
    return resolution.account;
  }

  /** One in-flight acquisition per scope set, shared by concurrent callers. */
  private inFlight = new Map<string, Promise<string | null>>();
  /** One no-account warning per session; the rest would bury it. */
  private warnedNoAccount = false;

  /**
   * Set when interactive recovery was attempted and REFUSED to start because its
   * budget is spent. Distinguishes "we are sending you to sign in" from "signing
   * in did not work twice already, so stop".
   */
  private recoveryExhausted = false;

  /** How many accounts MSAL can see — 0 distinguishes a cleared cache. */
  private accountCount(): number {
    try {
      return this.msalService?.instance?.getAllAccounts?.()?.length ?? -1;
    } catch {
      return -1;
    }
  }

  /**
   * Silent acquisition only. There is deliberately no interactive fallback here —
   * see the class doc.
   *
   * COALESCED, and that is the fix for the token-less export requests. A tab
   * fetches one family at a time, so by its second call the token is cached and
   * acquisition is instant. The PDF export fires temperature, loss and zone
   * together, so three acquisitions raced — and when the cached grant does not
   * already cover the scope, MSAL needs a hidden-iframe round trip that does not
   * survive being asked for three times at once. The losers threw, the catch
   * above turned each into "no bearer", and three history calls went out
   * unauthenticated.
   *
   * Sharing one promise means the first caller does the round trip and the rest
   * await its result, which is what a tab was getting by accident of timing.
   */
  private async acquireSilently(
    scopes: string[],
    account: unknown
  ): Promise<string | null> {
    const key = scopes.slice().sort().join(' ');
    const existing = this.inFlight.get(key);
    if (existing) {
      return existing;
    }

    const pending = (async () => {
      const result = await this.msalService.instance.acquireTokenSilent({
        scopes,
        account: account as never,
      });
      // A success means the grant is in place, so release the once-per-session
      // redirect latch. Without this a session gets exactly ONE redirect ever:
      // the cutover redirect consents the scope, the latch stays set, and a
      // genuine expiry later in the same session falls back silently instead of
      // recovering. Clearing on success is what makes the redirect repeatable
      // for real expiries while still bounding a scope that can never be granted.
      clearApiScopeRedirectLatch();
      return result?.accessToken ?? null;
    })();

    this.inFlight.set(key, pending);
    try {
      return await pending;
    } finally {
      // Cleared either way: a failure must not pin a rejected promise that every
      // later request then inherits.
      this.inFlight.delete(key);
    }
  }

  /**
   * The refusal we raise INSTEAD OF sending a request we know will be rejected.
   *
   * Both no-bearer branches below were written when the function key still
   * existed, so "pass through untouched" was a real second way to authenticate.
   * It is not any more: `?code=` is gone from the entire client, RequireEntraAuth
   * is on, and a keyless call gets `A validated bearer token is required; the
   * function key is not accepted here.` The old behaviour therefore spent its
   * retries asking the server a question the client already knew the answer to,
   * and reported it as a server problem.
   *
   * Shaped as an HttpErrorResponse with status 0 so classifyAuthorizationError
   * reads it as 'unavailable', never 'denied'. This is the client failing to
   * authenticate; it is not evidence about whether the user is entitled, and it
   * must never cache roles=[] or route anyone to /unauthorized.
   *
   * Only ever raised while requireEntraAuth() is true. With the flag off the
   * legacy pass-through still applies untouched.
   */
  private refuseUnauthenticated(
    req: HttpRequest<unknown>,
    why: string,
    /** True when interactive recovery is spent, so the UI must stop, not bounce. */
    recoveryExhausted = false
  ): HttpErrorResponse {
    const endpoint = this.endpointOf(req.url);
    console.error(
      `VericureAuth: NOT SENDING ${endpoint} — ${why}. This build authenticates ` +
        `to the Vericure API with an Entra bearer token only; there is no ` +
        `function key to fall back to, so an unauthenticated request could only ` +
        `come back 401. Sign in again to recover.`
    );
    // STATUS 0 IS NOT ENOUGH, and that is what made this a dead end for Joel.
    // No HTTP call happened, so 0 is honest — but every classifier in this app
    // reads 0 as "no response came back", i.e. offline, and offered "Try again":
    // a button that cannot fix an expired token. The body now carries an explicit
    // auth marker so the UI can offer SIGNING IN instead, and says whether
    // automatic recovery has already been spent.
    return new HttpErrorResponse({
      status: 0,
      statusText: 'Not signed in to the Vericure API',
      url: req.url,
      error: authFailureBody(
        why,
        `Not signed in to the Vericure API (${why}). The request to ${endpoint} ` +
          `was not sent.`,
        recoveryExhausted
      ),
    });
  }

  /**
   * Add X-Vericure-Client to VericureDataServices calls, and nothing else.
   *
   * Scoped to our own API on purpose. A custom header triggers a CORS preflight,
   * so putting it on third-party URLs (ipify, Graph) would add a round trip and
   * could fail outright against hosts that do not echo it. beta-dev's CORS was
   * verified to allow this header by name before shipping:
   *   OPTIONS with Access-Control-Request-Headers: authorization,x-vericure-client
   *   -> Access-Control-Allow-Headers: authorization,x-vericure-client
   */
  private stampClient(req: HttpRequest<unknown>): HttpRequest<unknown> {
    if (!isVericureApiUrl(req.url)) {
      return req;
    }
    return req.clone({ setHeaders: { [CLIENT_MARKER_HEADER]: clientMarker() } });
  }

  private withBearer(
    req: HttpRequest<unknown>,
    accessToken: string
  ): HttpRequest<unknown> {
    return req.clone({
      setHeaders: { Authorization: `Bearer ${accessToken}` },
    });
  }
}
